/**
 * TA-10772 (QA round 2) — a local record of "this user has already asked to
 * join this team".
 *
 * `getPublicTeam.hasPendingMemberAdditionRequest` is the only server signal
 * for that, and in the team link signup flow it comes back `false` even
 * though the request exists: the request is created during registration and
 * the flag has not caught up by the time the team detail page loads. The
 * button then offers "Request to Join" again — the exact symptom the ticket
 * was filed for, and why QA failed the first fix.
 *
 * The frontend already knows the truth at the moment it matters.
 * `createUserRequest` either resolves, or rejects with the duplicate code
 * 4104 (see @utils/userRequestErrors) — both mean a pending request exists.
 * Recording it here keeps the button correct for the rest of the browser
 * session: across the three tab-level `NonMemberContent` instances, which
 * each hold their own `useState`, and across a page reload.
 *
 * sessionStorage rather than localStorage, on purpose. The team link flow
 * already keeps all of its context there (`invitation_token`, `link_type`,
 * `team_preview_uuid`), and a per-tab lifetime bounds how long a stale entry
 * can survive. After an approval the user is a member and this screen never
 * renders again (`forgetPendingTeamJoinRequest` clears the entry then
 * anyway); after a decline they see "Pending Request" until the tab closes,
 * which is a much smaller error than silently inviting a duplicate request.
 *
 * The record is keyed by team alone, not by account, so it must not outlive
 * the session that wrote it: `AuthProvider.removeToken` calls
 * `clearPendingTeamJoinRequests` on logout. Without that, one user
 * requesting team A and then logging out would leave the next user to sign
 * in to the same tab looking at a disabled "Pending Request" for a request
 * that is not theirs.
 *
 * Every entry point is guarded: Safari private mode throws on the
 * `sessionStorage` getter itself, and a hand-edited value must not be able
 * to break the render. Failure always degrades to "nothing remembered",
 * which is the pre-existing behaviour.
 */

import { isDuplicatePendingRequestError } from '@utils/userRequestErrors';

import type { ApolloError } from '@apollo/client';

const STORAGE_KEY = 'pending_team_join_requests';

const getStorage = (): Storage | undefined => {
  try {
    if (typeof window === 'undefined') return undefined;
    return window.sessionStorage ?? undefined;
  } catch {
    return undefined;
  }
};

const readAll = (): string[] => {
  const storage = getStorage();
  if (!storage) return [];

  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return [];

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed.filter(
      (entry): entry is string => typeof entry === 'string' && entry !== ''
    );
  } catch {
    return [];
  }
};

const writeAll = (teamUUIds: string[]) => {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(teamUUIds));
  } catch {
    // Quota or private mode. The button falls back to the server flag.
  }
};

/**
 * Record that a join request exists for this team. Call it only where that
 * is known for certain: a resolved `createUserRequest`, or one rejected as a
 * duplicate.
 */
export const rememberPendingTeamJoinRequest = (
  teamUUId?: string | null
): void => {
  if (!teamUUId) return;

  const remembered = readAll();
  if (remembered.includes(teamUUId)) return;

  writeAll([...remembered, teamUUId]);
};

/** Whether a join request was recorded for this team in this tab. */
export const hasPendingTeamJoinRequest = (teamUUId?: string | null): boolean =>
  !!teamUUId && readAll().includes(teamUUId);

/**
 * Drop the record — the request is resolved. Called once membership is
 * confirmed, so a user who later leaves the team is offered the button again.
 */
export const forgetPendingTeamJoinRequest = (
  teamUUId?: string | null
): void => {
  if (!teamUUId) return;

  const remembered = readAll();
  if (!remembered.includes(teamUUId)) return;

  writeAll(remembered.filter((entry) => entry !== teamUUId));
};

/**
 * Drop every record. Called from `AuthProvider.removeToken`, which is the
 * one place that knows the account behind these entries is going away — on
 * an explicit logout and on the 401 path alike. Nothing here is keyed by
 * user, so a leftover entry would follow the next user to sign in to this
 * tab (TA-10772 QA round 2 review).
 */
export const clearPendingTeamJoinRequests = (): void => {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // Private mode. Nothing was stored to begin with.
  }
};

/**
 * Record the outcome of a `createUserRequest` for a team, and report whether
 * the user now has a pending request.
 *
 * Both callers apply the same rule, so it lives here rather than being
 * re-derived: the mutation resolving means pending, and so does a rejection
 * with the duplicate code 4104 — the request already exists, which is what
 * the team link registration path creates on the backend. Any other failure
 * is neither recorded nor reported as pending, so "Request to Join" stays
 * actionable and the caller can surface the error.
 *
 * `error` is `unknown` because the post-signup effect catches a rejected
 * promise rather than receiving Apollo's typed `onError` argument.
 */
export const recordTeamJoinRequestOutcome = ({
  teamUUId,
  error,
}: {
  teamUUId?: string | null;
  error?: unknown;
}): boolean => {
  const isPending =
    !error || isDuplicatePendingRequestError(error as ApolloError);

  if (isPending) rememberPendingTeamJoinRequest(teamUUId);

  return isPending;
};

/**
 * Send a join request and record its outcome.
 *
 * The post-signup effect in app-stack.web awaits the raw mutation, so
 * without this both of its branches — resolved, and rejected as a duplicate
 * — had to remember to call `recordTeamJoinRequestOutcome` themselves, with
 * nothing but the effect (untestable in place) holding them together. The
 * try/catch lives here instead, so the rule is exercised by this module's
 * own tests.
 *
 * `createRequest` is a thunk rather than the mutation itself: the caller
 * owns the variables and its `refetchQueries`, which differ between the
 * button on the team page and the post-signup flow.
 *
 * Returns whether the user now has a pending request, i.e. whether a record
 * was written. A genuine failure returns `false` and rethrows nothing — the
 * team link flow must navigate either way.
 */
export const submitTeamJoinRequest = async ({
  teamUUId,
  createRequest,
}: {
  teamUUId?: string | null;
  createRequest: () => Promise<unknown>;
}): Promise<boolean> => {
  try {
    await createRequest();
    return recordTeamJoinRequestOutcome({ teamUUId });
  } catch (error) {
    return recordTeamJoinRequestOutcome({ teamUUId, error });
  }
};
